import { ILocationMenuItem } from "../operationRuleLocations/OperationRuleLocationModel";

/**
 * Checks whether an item has an explicit ordering position.
 * Items with order > 0 are considered explicitly ordered.
 * Items with order null, undefined or -1 have no explicit order.
 */
const hasExplicitOrder = (order?: number | null): boolean => (order ?? -1) > 0;

/**
 * Stable sort that respects an optional `order` field.
 *
 * - Items with `order > 0` are placed first, sorted ascending.
 * - Items without explicit order (null, undefined, -1) keep
 *   their original position relative to each other (backend order).
 */
function stableSortByOrder<T extends { order?: number }>(items: T[]): T[] {
    return items
        .map((item, originalIndex) => ({ item, originalIndex }))
        .sort((a, b) => {
            const aOrdered = hasExplicitOrder(a.item.order);
            const bOrdered = hasExplicitOrder(b.item.order);

            if (aOrdered && bOrdered) return a.item.order! - b.item.order!;
            if (aOrdered) return -1;
            if (bOrdered) return 1;
            return a.originalIndex - b.originalIndex;
        })
        .map(({ item }) => item);
}

/**
 * Sorts a location menu list and its nested events by the `order` field.
 *
 * Applied at two levels:
 * 1. Events (items) within each location
 * 2. Locations themselves
 */
export function sortLocationMenuList(
    menuList: ILocationMenuItem[],
): ILocationMenuItem[] {
    const withSortedItems = menuList.map((location) => ({
        ...location,
        items:
            location.items.length > 1
                ? stableSortByOrder(location.items)
                : location.items,
    }));

    return stableSortByOrder(withSortedItems);
}
